/**
 * "Under review" / waiting-for-approval lock screen (feature #2/#3).
 *
 * FREE-TIER MODEL: the user is always signed in when they land here (the users
 * doc is created post-OTP with status 'pending'). There is no more
 * `checkMobileStatus` callable — approval detection is entirely realtime:
 *
 *   - AuthContext's users/{uid} listener reflects status changes live. When an
 *     admin flips status to 'approved', `status` updates here and we navigate to
 *     Home automatically (no user action needed).
 *   - On app resume we simply re-assert current status (the listener keeps the
 *     snapshot warm); the "Check again" button just reports the latest status.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CenteredScreen } from '../../components/Screen';
import { useAuth } from './AuthContext';
import { useAppResume } from '../../hooks/useAppResume';
import { ROUTES } from '../../routes/paths';

export function UnderReviewScreen(): JSX.Element {
  const { status, logout } = useAuth();
  const navigate = useNavigate();
  const [note, setNote] = useState<string | null>(null);

  // React to realtime status changes from the AuthContext listener.
  useEffect(() => {
    if (status === 'approved') navigate(ROUTES.home, { replace: true });
    else if (status === 'blocked' || status === 'rejected')
      navigate(ROUTES.blocked, { replace: true });
  }, [status, navigate]);

  // The users/{uid} listener already keeps status live; "recheck" just surfaces
  // the current state to the user (and reacts to the effect above on resume).
  const recheck = useCallback(() => {
    if (status === 'pending' || status === 'unknown') {
      setNote('Still pending approval. We will let you know as soon as it is approved.');
    }
  }, [status]);

  useAppResume(recheck);

  return (
    <CenteredScreen
      title="Under review"
      subtitle="Your registration is being reviewed by our team."
    >
      <p className="text-sm text-slate-600">
        You will get access as soon as an admin approves your account. This usually takes a
        little while.
      </p>
      {note ? <p className="mt-3 text-sm text-brand-dark">{note}</p> : null}
      <button
        type="button"
        className="btn-primary mt-5 w-full"
        onClick={recheck}
      >
        Check again
      </button>
      <button
        type="button"
        className="btn-secondary mt-3 w-full"
        onClick={async () => {
          await logout();
          navigate(ROUTES.login, { replace: true });
        }}
      >
        Sign out
      </button>
    </CenteredScreen>
  );
}
